import { useMemo } from 'react';
import { useTranslation } from 'react-i18next';

import PropTypes from 'prop-types';
import { generateDiffStrings } from 'utils/func/diffString';
import DiffColumn from '../DiffColumn';

export default function KPICompareTable({ researchKpis1, researchKpis2, diffObj }) {
  const { t } = useTranslation();

  const groupedKpis1 = useMemo(() => {
    const groups = [];

    (researchKpis1 ?? [])?.forEach((kpi) => {
      const key = kpi?.researchKpi?.researchKpiGroup?.name;
      if (!groups[key]) groups[key] = [];
      groups[key].push(kpi);
    });

    return Object.values(groups);
  }, [researchKpis1]);

  const groupedKpis2 = useMemo(() => {
    const groups = [];

    (researchKpis2 ?? [])?.forEach((kpi) => {
      const key = kpi?.researchKpi?.researchKpiGroup?.name;
      if (!groups[key]) groups[key] = [];
      groups[key].push(kpi);
    });

    return Object.values(groups);
  }, [researchKpis2]);

  return (
    <tr>
      <td className="py-2">
        <div
          className="p-datatable p-component p-datatable-responsive-scroll p-datatable-scrollable p-datatable-gridlines p-datatable-gridlines"
          data-scrollselectors=".p-datatable-wrapper"
        >
          <div className="p-datatable-wrapper">
            <table className="p-datatable-table">
              <thead className="p-datatable-thead">
                <tr>
                  <th className="w-4rem text-center" style={{ minWidth: '4rem' }}>
                    {t('projectProposal.researchResult.kpis.fields.order')}
                  </th>
                  <th className="w-16rem text-center" style={{ minWidth: '8rem' }}>
                    {t('projectProposal.researchResult.kpis.fields.KPI')}
                  </th>
                  <th className="w-6rem text-center" style={{ minWidth: '4rem' }}>
                    {t('projectProposal.researchResult.kpis.fields.numberRegistration')}
                  </th>
                  <th className="w-16rem text-center" style={{ minWidth: '8rem' }}>
                    {t('projectProposal.researchResult.kpis.fields.content')}
                  </th>
                </tr>
              </thead>
              {groupedKpis1?.map((kpiGroup, groupIndex) => (
                <tbody
                  className="p-datatable-tbody"
                  key={kpiGroup[0]?.researchKpi?.researchKpiGroup?.id}
                >
                  {kpiGroup?.map((kpi, index) => (
                    <tr key={kpi?.id ?? index}>
                      {index === 0 ? (
                        <td className="text-center font-bold" rowSpan={kpiGroup?.length}>
                          {
                            generateDiffStrings(
                              kpi?.researchKpi?.researchKpiGroup?.name,
                              groupedKpis2?.[groupIndex]?.[index]?.researchKpi?.researchKpiGroup
                                ?.name
                            )?.left
                          }
                        </td>
                      ) : (
                        <td className="hidden" />
                      )}
                      <td>
                        {
                          generateDiffStrings(
                            t('lang') === 'vi'
                              ? kpi?.researchKpi.name
                              : kpi?.researchKpi.englishName,
                            t('lang') === 'vi'
                              ? groupedKpis2?.[groupIndex]?.[index]?.researchKpi.name
                              : groupedKpis2?.[groupIndex]?.[index]?.researchKpi.englishName
                          )?.left
                        }
                      </td>
                      <td className="text-center">
                        {
                          generateDiffStrings(
                            `${kpi?.quantity ?? ''}`,
                            `${groupedKpis2?.[groupIndex]?.[index]?.quantity ?? ''}`
                          )?.left
                        }
                      </td>
                      <td>
                        {
                          generateDiffStrings(
                            kpi?.content,
                            groupedKpis2?.[groupIndex]?.[index]?.content
                          )?.left
                        }
                      </td>
                    </tr>
                  ))}
                </tbody>
              ))}
            </table>
          </div>
        </div>
      </td>
      <td className="py-2">
        <div
          className="p-datatable p-component p-datatable-responsive-scroll p-datatable-scrollable p-datatable-gridlines p-datatable-gridlines"
          data-scrollselectors=".p-datatable-wrapper"
        >
          <div className="p-datatable-wrapper">
            <table className="p-datatable-table">
              <thead className="p-datatable-thead">
                <tr>
                  <th className="w-4rem text-center" style={{ minWidth: '4rem' }}>
                    {t('projectProposal.researchResult.kpis.fields.order')}
                  </th>
                  <th className="w-16rem text-center" style={{ minWidth: '8rem' }}>
                    {t('projectProposal.researchResult.kpis.fields.KPI')}
                  </th>
                  <th className="w-6rem text-center" style={{ minWidth: '4rem' }}>
                    {t('projectProposal.researchResult.kpis.fields.numberRegistration')}
                  </th>
                  <th className="w-16rem text-center" style={{ minWidth: '8rem' }}>
                    {t('projectProposal.researchResult.kpis.fields.content')}
                  </th>
                </tr>
              </thead>
              {groupedKpis2?.map((kpiGroup, groupIndex) => (
                <tbody
                  className="p-datatable-tbody"
                  key={kpiGroup[0]?.researchKpi?.researchKpiGroup?.id}
                >
                  {kpiGroup?.map((kpi, index) => (
                    <tr key={kpi?.id ?? index}>
                      {index === 0 ? (
                        <td className="text-center font-bold" rowSpan={kpiGroup?.length}>
                          {
                            generateDiffStrings(
                              groupedKpis1?.[groupIndex]?.[index]?.researchKpi?.researchKpiGroup
                                ?.name,
                              kpi?.researchKpi?.researchKpiGroup?.name
                            )?.right
                          }
                        </td>
                      ) : (
                        <td className="hidden" />
                      )}
                      <td>
                        {
                          generateDiffStrings(
                            t('lang') === 'vi'
                              ? groupedKpis1?.[groupIndex]?.[index]?.researchKpi.name
                              : groupedKpis1?.[groupIndex]?.[index]?.researchKpi.englishName,
                            t('lang') === 'vi'
                              ? kpi?.researchKpi.name
                              : kpi?.researchKpi.englishName
                          )?.right
                        }
                      </td>
                      <td className="text-center">
                        {
                          generateDiffStrings(
                            `${groupedKpis1?.[groupIndex]?.[index]?.quantity ?? ''}`,
                            `${kpi?.quantity ?? ''}`
                          )?.right
                        }
                      </td>
                      <td>
                        {
                          generateDiffStrings(
                            groupedKpis1?.[groupIndex]?.[index]?.content,
                            kpi?.content
                          )?.right
                        }
                      </td>
                    </tr>
                  ))}
                </tbody>
              ))}
            </table>
          </div>
        </div>
      </td>
      <DiffColumn
        isDifferent={Object.values(diffObj ?? {})?.some(
          (e) => e === true || e?.quantity === true || e?.content === true
        )}
      />
    </tr>
  );
}

KPICompareTable.propTypes = {
  // eslint-disable-next-line react/forbid-prop-types
  researchKpis1: PropTypes.array.isRequired,
  // eslint-disable-next-line react/forbid-prop-types
  researchKpis2: PropTypes.array.isRequired,
  // eslint-disable-next-line react/forbid-prop-types
  diffObj: PropTypes.array.isRequired,
};
